import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Search, MapPin, Stethoscope, Phone, Filter, 
  Calendar, Coffee, Utensils, Users, Building,
  ArrowRight, Network, ChevronDown, X, Clock, Send,
  ShieldCheck, Crown, UserPlus, CircleCheck, Fingerprint
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import MarketSelect from "../components/MarketSelect";
import { getStoredMarket, setStoredMarket } from "../constants/markets";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast, Toaster } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PublicDirectory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [city, setCity] = useState("");
  const [market, setMarket] = useState(
    searchParams.get("market") || getStoredMarket() || ""
  );
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimProvider, setClaimProvider] = useState(null);

  const handleMarketChange = (v) => {
    setMarket(v);
    setStoredMarket(v);
    setCity(""); // clear city refinement when market changes
  };

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (specialty) params.append("specialty", specialty);
      if (city) params.append("city", city);
      else if (market) params.append("market", market);
      params.append("limit", "100");
      
      const response = await axios.get(`${API}/public/providers?${params}`);
      setProviders(response.data.providers);
    } catch (error) {
      toast.error("Unable to load providers");
    } finally {
      setLoading(false);
    }
  }, [search, specialty, city, market]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const specialties = [
    { label: "Internal Medicine", value: "Internal Medicine" },
    { label: "Family Medicine", value: "Family Medicine" },
    { label: "Pediatrics", value: "Pediatrics" },
    { label: "Orthopedic Surgery", value: "Orthopaedic" },
    { label: "Vascular Surgery", value: "Vascular" },
    { label: "OB/GYN", value: "Obstetrics" },
    { label: "Cardiology", value: "Cardiovascular" },
    { label: "Dermatology", value: "Dermatology" },
    { label: "Neurology", value: "Neurology" },
    { label: "Psychiatry", value: "Psychiatry" },
    { label: "Emergency Medicine", value: "Emergency Medicine" },
    { label: "General Surgery", value: "Surgery" },
    { label: "Ophthalmology", value: "Ophthalmology" },
    { label: "Urology", value: "Urology" },
    { label: "Anesthesiology", value: "Anesthesiology" },
    { label: "Radiology", value: "Radiology" },
    { label: "Physical Therapy", value: "Physical Therapist" },
    { label: "Nurse Practitioner", value: "Nurse Practitioner" },
    { label: "Dentist", value: "Dentist" },
    { label: "Chiropractor", value: "Chiropractor" },
    { label: "Podiatrist", value: "Podiatrist" },
    { label: "Pain Medicine", value: "Pain Medicine" },
    { label: "Otolaryngology (ENT)", value: "Otolaryngology" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" 
                alt="iConcierge Logo" 
                className="h-10 w-auto"
              />
            </Link>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/browse" className="text-primary font-medium">Browse Providers</Link>
              <Link to="/book" className="text-slate-600 hover:text-primary">Book a Meeting</Link>
              <Link to="/#pricing" className="text-slate-600 hover:text-primary">Pricing</Link>
            </nav>

            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate("/login")}>
                Sign In
              </Button>
              <Button onClick={() => navigate("/register")} className="bg-primary text-white hover:bg-primary/90 rounded-full">
                Join Network
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary/5 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Find & Connect with Healthcare Providers
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            Browse our network of physicians, clinicians, and healthcare professionals. 
            Schedule lunches, meetings, and in-services directly.
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name or specialty..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12"
              />
            </div>
            
            <Select value={specialty || "all"} onValueChange={(v) => setSpecialty(v === "all" ? "" : v)}>
              <SelectTrigger className="h-12">
                <Stethoscope className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Specialties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Specialties</SelectItem>
                {specialties.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <MarketSelect
              value={market}
              onChange={handleMarketChange}
              variant="filter"
              testId="directory-market-select"
              placeholder="All Markets"
            />
          </div>
          {market && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <Badge className="bg-primary/10 text-primary">
                <MapPin className="w-3 h-3 mr-1" />
                {market === "DFW" ? "Dallas–Fort Worth, TX" : market}
              </Badge>
              <button
                onClick={() => handleMarketChange("")}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="clear-market-btn"
              >
                Show all markets
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Provider Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-600">
            <span className="font-semibold text-slate-900">{providers.length}</span> providers found
          </p>
          {(search || specialty || city || market) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setSearch(""); setSpecialty(""); setCity(""); handleMarketChange(""); }}
            >
              <X className="w-4 h-4 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-slate-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : providers.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map((provider, index) => (
              <motion.div
                key={provider.npi || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`bg-white rounded-xl border p-6 hover:shadow-lg transition-all cursor-pointer ${
                  provider.is_member 
                    ? "border-amber-300 hover:border-amber-400 ring-1 ring-amber-100" 
                    : "border-slate-200 hover:border-primary/50"
                }`}
                onClick={() => { setSelectedProvider(provider); setShowBookingModal(true); }}
                data-testid={`provider-card-${provider.npi || index}`}
              >
                {provider.is_member && (
                  <div className="flex items-center gap-1.5 mb-3" data-testid="member-badge">
                    <div className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-200">
                      <Crown className="w-3 h-3" />
                      iConcierge Member
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-4 mb-4">
                  <div className="relative">
                    <Avatar className="w-16 h-16">
                      <AvatarFallback className={`text-xl ${
                        provider.is_member 
                          ? "bg-amber-100 text-amber-700" 
                          : "bg-primary/10 text-primary"
                      }`}>
                        {provider.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    {provider.is_member && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center border-2 border-white">
                        <ShieldCheck className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 text-lg">
                      {provider.name}
                      {provider.credential && <span className="text-slate-500 font-normal">, {provider.credential}</span>}
                    </h3>
                    <p className="text-primary font-medium">{provider.specialty || 'Healthcare Provider'}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {provider.city && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {provider.city}, {provider.state}
                    </div>
                  )}
                  {provider.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {provider.phone}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-2">
                  <Button 
                    className="w-full bg-primary text-white hover:bg-primary/90 rounded-full"
                    onClick={(e) => { e.stopPropagation(); setSelectedProvider(provider); setShowBookingModal(true); }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Meeting
                  </Button>
                  {!provider.is_member && !provider.is_registered && (
                    <Button 
                      variant="ghost"
                      className="w-full text-primary hover:bg-primary/5 rounded-full text-sm"
                      onClick={(e) => { e.stopPropagation(); setClaimProvider(provider); setShowClaimModal(true); }}
                      data-testid={`claim-btn-${provider.npi}`}
                    >
                      <Fingerprint className="w-4 h-4 mr-2" />
                      Is this you? Claim profile
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {market && market !== "DFW"
                ? `No providers yet in ${market === "New York" ? "New York" : market}`
                : "No providers found"}
            </h3>
            <p className="text-slate-600 mb-6">
              {market && market !== "DFW"
                ? "Be the first to add a provider to this market — your network starts here."
                : "Try adjusting your search or filters, or add them to our directory"}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => { setSearch(""); setSpecialty(""); setCity(""); handleMarketChange(""); }}>
                Clear All Filters
              </Button>
              <Button onClick={() => setShowSubmitModal(true)} className="bg-primary text-white hover:bg-primary/90 rounded-full" data-testid="add-provider-empty-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add a Provider
              </Button>
            </div>
          </div>
        )}

        {/* Provider Not Listed Banner */}
        {providers.length > 0 && (
          <div className="mt-10 bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-8 text-center">
            <UserPlus className="w-10 h-10 mx-auto mb-3 text-primary/60" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Can't find a provider?</h3>
            <p className="text-slate-600 mb-4 max-w-md mx-auto">
              Help us grow the directory. Submit their info and we'll add them.
            </p>
            <Button 
              onClick={() => setShowSubmitModal(true)} 
              variant="outline"
              className="rounded-full border-primary text-primary hover:bg-primary/5"
              data-testid="add-provider-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Submit a Provider
            </Button>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-primary/5 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Are You a Healthcare Provider?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join our network to receive meeting requests, connect with peers, and grow your practice.
          </p>
          <Button onClick={() => navigate("/register")} size="lg" className="bg-primary text-white hover:bg-primary/90 rounded-full px-8">
            Join iConcierge Network
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" 
              alt="iConcierge Logo" 
              className="h-8 w-auto"
            />
          </div>
          <p className="text-slate-500 text-sm">
            © 2025 iConcierge Healthcare Marketing. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal 
        open={showBookingModal}
        onClose={() => { setShowBookingModal(false); setSelectedProvider(null); }}
        provider={selectedProvider}
      />

      {/* Submit Provider Modal */}
      <SubmitProviderModal
        open={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onSuccess={() => fetchProviders()}
        specialties={specialties}
      />

      {/* Claim Profile Modal */}
      <ClaimProfileModal
        open={showClaimModal}
        onClose={() => { setShowClaimModal(false); setClaimProvider(null); }}
        provider={claimProvider}
        onSuccess={() => fetchProviders()}
      />
    </div>
  );
};

// Booking Modal Component
const BookingModal = ({ open, onClose, provider }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    meeting_type: "lunch",
    date: "",
    time: "",
    your_name: "",
    your_email: "",
    your_phone: "",
    your_company: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/public/book-meeting`, {
        ...formData,
        provider_npi: provider?.npi,
        provider_name: provider?.name
      });
      toast.success("Meeting request sent! They'll be in touch soon.");
      onClose();
      setStep(1);
      setFormData({
        meeting_type: "lunch",
        date: "",
        time: "",
        your_name: "",
        your_email: "",
        your_phone: "",
        your_company: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-primary/10 text-primary">
                {provider.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-lg">Schedule with</p>
              <p className="text-primary font-normal">{provider.name}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {step === 1 && (
            <>
              <div>
                <Label>Meeting Type *</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {[
                    { value: "lunch", label: "Lunch", icon: Utensils },
                    { value: "breakfast", label: "Breakfast", icon: Coffee },
                    { value: "coffee", label: "Coffee", icon: Coffee },
                    { value: "in_service", label: "In-Service", icon: Users },
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({...formData, meeting_type: type.value})}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        formData.meeting_type === type.value 
                          ? 'border-primary bg-primary/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <type.icon className={`w-6 h-6 ${formData.meeting_type === type.value ? 'text-primary' : 'text-slate-400'}`} />
                      <span className={formData.meeting_type === type.value ? 'text-primary font-medium' : 'text-slate-600'}>
                        {type.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Preferred Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Preferred Time *</Label>
                  <Select value={formData.time || "select"} onValueChange={(v) => setFormData({...formData, time: v === "select" ? "" : v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select" disabled>Select time</SelectItem>
                      <SelectItem value="08:00">8:00 AM</SelectItem>
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="10:00">10:00 AM</SelectItem>
                      <SelectItem value="11:00">11:00 AM</SelectItem>
                      <SelectItem value="11:30">11:30 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="12:30">12:30 PM</SelectItem>
                      <SelectItem value="13:00">1:00 PM</SelectItem>
                      <SelectItem value="14:00">2:00 PM</SelectItem>
                      <SelectItem value="15:00">3:00 PM</SelectItem>
                      <SelectItem value="16:00">4:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                type="button" 
                onClick={() => setStep(2)}
                className="w-full bg-primary text-white"
                disabled={!formData.date || !formData.time}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <Label>Your Name *</Label>
                <Input
                  value={formData.your_name}
                  onChange={(e) => setFormData({...formData, your_name: e.target.value})}
                  placeholder="John Smith"
                  required
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.your_email}
                    onChange={(e) => setFormData({...formData, your_email: e.target.value})}
                    placeholder="john@company.com"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={formData.your_phone}
                    onChange={(e) => setFormData({...formData, your_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Company/Organization</Label>
                <Input
                  value={formData.your_company}
                  onChange={(e) => setFormData({...formData, your_company: e.target.value})}
                  placeholder="ABC Pharmaceuticals"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Message (Optional)</Label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell them about your purpose for the meeting..."
                  className="w-full mt-1 min-h-[80px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button type="submit" className="flex-1 bg-primary text-white" disabled={loading}>
                  {loading ? "Sending..." : "Send Request"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PublicDirectory;

// Submit Provider Modal Component
const SubmitProviderModal = ({ open, onClose, onSuccess, specialties }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [npiVerified, setNpiVerified] = useState(false);
  const [formData, setFormData] = useState({
    provider_name: "",
    npi_number: "",
    specialty: "",
    city: "",
    state: "TX",
    phone: "",
    practice_name: "",
    submitter_name: "",
    submitter_email: "",
    submitter_company: "",
    notes: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/public/submit-provider`, formData);
      setNpiVerified(response.data.npi_verified);
      setSubmitted(true);
      toast.success(response.data.message);
      onSuccess();
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setNpiVerified(false);
    setFormData({
      provider_name: "", npi_number: "", specialty: "", city: "", state: "TX",
      phone: "", practice_name: "", submitter_name: "", submitter_email: "",
      submitter_company: "", notes: "",
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            {submitted ? "Provider Submitted" : "Submit a Provider"}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-6" data-testid="submit-provider-success">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              npiVerified ? "bg-emerald-100" : "bg-blue-100"
            }`}>
              <CircleCheck className={`w-8 h-8 ${npiVerified ? "text-emerald-600" : "text-blue-600"}`} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {npiVerified ? "Provider Added!" : "Submission Received!"}
            </h3>
            <p className="text-slate-600 mb-6">
              {npiVerified
                ? "We verified the NPI and added them to the directory. They're searchable now."
                : "We'll verify the information and add them to the directory shortly."}
            </p>
            <Button onClick={handleClose} className="bg-primary text-white hover:bg-primary/90 rounded-full">
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="submit-provider-form">
            <p className="text-sm text-slate-500">Help us grow the directory by submitting a provider you'd like to connect with.</p>

            <div className="border-b border-slate-100 pb-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Provider Info</p>
            </div>

            <div>
              <Label>Provider Name *</Label>
              <Input
                value={formData.provider_name}
                onChange={(e) => setFormData({...formData, provider_name: e.target.value})}
                placeholder="Dr. John Smith"
                required
                className="mt-1"
                data-testid="submit-provider-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>NPI Number</Label>
                <Input
                  value={formData.npi_number}
                  onChange={(e) => setFormData({...formData, npi_number: e.target.value})}
                  placeholder="1234567890"
                  className="mt-1"
                  data-testid="submit-provider-npi"
                />
                <p className="text-xs text-slate-400 mt-1">If known — helps auto-verify</p>
              </div>
              <div>
                <Label>Specialty *</Label>
                <Select value={formData.specialty || "pick"} onValueChange={(v) => setFormData({...formData, specialty: v === "pick" ? "" : v})}>
                  <SelectTrigger className="mt-1" data-testid="submit-provider-specialty">
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pick" disabled>Select specialty</SelectItem>
                    {specialties.map((s) => (
                      <SelectItem key={s.value} value={s.label}>{s.label}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Your city"
                  required
                  className="mt-1"
                  data-testid="submit-provider-city"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  placeholder="TX"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Practice/Group Name</Label>
              <Input
                value={formData.practice_name}
                onChange={(e) => setFormData({...formData, practice_name: e.target.value})}
                placeholder="Orthopedic Specialists Group"
                className="mt-1"
                data-testid="submit-provider-practice"
              />
            </div>

            <div className="border-b border-slate-100 pb-2 pt-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Your Info</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Your Name *</Label>
                <Input
                  value={formData.submitter_name}
                  onChange={(e) => setFormData({...formData, submitter_name: e.target.value})}
                  placeholder="Jane Doe"
                  required
                  className="mt-1"
                  data-testid="submit-submitter-name"
                />
              </div>
              <div>
                <Label>Your Email *</Label>
                <Input
                  type="email"
                  value={formData.submitter_email}
                  onChange={(e) => setFormData({...formData, submitter_email: e.target.value})}
                  placeholder="jane@company.com"
                  required
                  className="mt-1"
                  data-testid="submit-submitter-email"
                />
              </div>
            </div>

            <div>
              <Label>Your Company</Label>
              <Input
                value={formData.submitter_company}
                onChange={(e) => setFormData({...formData, submitter_company: e.target.value})}
                placeholder="ABC Pharmaceuticals"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional info about this provider..."
                className="w-full mt-1 min-h-[60px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm resize-none"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-full"
              disabled={loading || !formData.provider_name || !formData.specialty || !formData.city || !formData.submitter_name || !formData.submitter_email}
              data-testid="submit-provider-btn"
            >
              {loading ? "Submitting..." : "Submit Provider"}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};


// Claim Profile Modal Component
const ClaimProfileModal = ({ open, onClose, provider, onSuccess }) => {
  const [step, setStep] = useState("confirm"); // confirm | register | claiming | done
  const [loading, setLoading] = useState(false);
  const [registerData, setRegisterData] = useState({ email: "", password: "", name: "" });
  const [authToken, setAuthToken] = useState(() => sessionStorage.getItem("token") || localStorage.getItem("token"));

  const isLoggedIn = !!authToken;

  const handleClaim = async () => {
    if (!provider?.npi) return;
    setLoading(true);
    setStep("claiming");

    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.post(
        `${API}/providers/claim/${provider.npi}`,
        {},
        { headers, withCredentials: true }
      );
      toast.success(response.data.message);
      setStep("done");
      onSuccess();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to claim profile";
      toast.error(msg);
      setStep("confirm");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAndClaim = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const regResponse = await axios.post(`${API}/auth/register`, {
        email: registerData.email,
        password: registerData.password,
        name: registerData.name || provider?.name?.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '),
      });
      const newToken = regResponse.data.token;
      setAuthToken(newToken);
      sessionStorage.setItem("token", newToken);

      // Now claim with the new token
      setStep("claiming");
      const claimResponse = await axios.post(
        `${API}/providers/claim/${provider.npi}`,
        {},
        { headers: { Authorization: `Bearer ${newToken}` }, withCredentials: true }
      );
      toast.success(claimResponse.data.message);
      setStep("done");
      onSuccess();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to register or claim";
      toast.error(msg);
      setStep("register");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("confirm");
    setRegisterData({ email: "", password: "", name: "" });
    onClose();
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-primary" />
            Claim Your Profile
          </DialogTitle>
        </DialogHeader>

        {step === "done" ? (
          <div className="text-center py-6" data-testid="claim-success">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CircleCheck className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Profile Claimed!</h3>
            <p className="text-slate-600 mb-6">
              Your listing is now linked to your account. Update your profile, receive meeting requests, and connect with others on the platform.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                onClick={() => { handleClose(); window.location.href = "/profile"; }}
              >
                Complete Your Profile
              </Button>
              <Button variant="ghost" onClick={handleClose}>
                Stay Here
              </Button>
            </div>
          </div>
        ) : step === "claiming" ? (
          <div className="text-center py-8" data-testid="claim-loading">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Linking your account...</p>
          </div>
        ) : step === "register" ? (
          <form onSubmit={handleRegisterAndClaim} className="space-y-4" data-testid="claim-register-form">
            <div className="bg-primary/5 border border-primary/10 rounded-xl p-4 mb-2">
              <p className="text-sm font-medium text-slate-900">{provider.name}</p>
              <p className="text-xs text-primary">{provider.specialty}</p>
              <p className="text-xs text-slate-500">{provider.city}, {provider.state}</p>
              {provider.npi && <p className="text-xs text-slate-400 mt-1">NPI: {provider.npi}</p>}
            </div>

            <p className="text-sm text-slate-600">Create an account to claim this profile:</p>

            <div>
              <Label>Your Name</Label>
              <Input
                value={registerData.name}
                onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                placeholder={provider.name?.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                className="mt-1"
                data-testid="claim-name-input"
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={registerData.email}
                onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                placeholder="you@clinic.com"
                required
                className="mt-1"
                data-testid="claim-email-input"
              />
            </div>

            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={registerData.password}
                onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                placeholder="Create a password"
                required
                className="mt-1"
                data-testid="claim-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-full"
              disabled={loading || !registerData.email || !registerData.password}
              data-testid="claim-register-btn"
            >
              {loading ? "Creating Account..." : "Create Account & Claim"}
            </Button>

            <p className="text-center text-xs text-slate-500">
              Already have an account?{" "}
              <a href="/login" className="text-primary hover:underline">Sign in first</a>, then come back to claim.
            </p>
          </form>
        ) : (
          <div className="space-y-4" data-testid="claim-confirm">
            <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-primary/10 text-primary text-lg">
                    {provider.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-slate-900">{provider.name}</p>
                  <p className="text-sm text-primary">{provider.specialty}</p>
                  <p className="text-xs text-slate-500">{provider.city}, {provider.state}</p>
                  {provider.npi && <p className="text-xs text-slate-400">NPI: {provider.npi}</p>}
                </div>
              </div>
            </div>

            <p className="text-sm text-slate-600">
              Claiming this profile will link it to your account. You'll be able to:
            </p>
            <ul className="space-y-1.5">
              {[
                "Update your professional information",
                "Receive meeting & lunch requests",
                "Message other providers on the platform",
                "Appear as a verified member in the directory",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-slate-700">
                  <CircleCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            {isLoggedIn ? (
              <Button
                className="w-full bg-primary text-white hover:bg-primary/90 rounded-full"
                onClick={handleClaim}
                disabled={loading}
                data-testid="claim-confirm-btn"
              >
                <Fingerprint className="w-4 h-4 mr-2" />
                Claim This Profile
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full"
                  onClick={() => setStep("register")}
                  data-testid="claim-signup-btn"
                >
                  Sign Up & Claim
                </Button>
                <p className="text-center text-xs text-slate-500">
                  Already have an account?{" "}
                  <a href="/login" className="text-primary hover:underline">Sign in first</a>
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};